import { Controller } from 'stimulus';
import Chart from 'chart.js/auto';
import { hourLabels, seriesByHour } from './shared/labels';
import { currentLocale, t } from '../../javascript/lib/i18n';
import { currentTheme } from '../chart_controller/plugins/background_color_plugin';

const COLOR_BLACK = '#000';
const COLOR_GRAY = '#AAAFB5';
const COLOR_GRAY_LIGHT = '#E5E5E5';
const COLOR_GRAY_DARK = '#6A6A6A';
const COLOR_BLUE = '#0866F7';
const COLOR_BLUE_LIGHT = '#2C63EE';

export default class extends Controller {
  static targets = ['canvas'];
  static values = { data: Object, url: String };

  disconnect() {
    this.chart.destroy();

    document.removeEventListener(
      'amba:theme_changed',
      this.reInitializeOnColorChange
    );
  }

  connect() {
    document.addEventListener(
      'amba:theme_changed',
      this.reInitializeOnColorChange
    );
    this.initializeChart();
  }

  reInitializeOnColorChange = () => {
    this.chart.destroy();
    this.initializeChart();
  };

  initializeChart() {
    const labels = hourLabels(currentLocale());

    this.chart = new Chart(this.canvasTarget, {
      type: 'bar',
      options: {
        interaction: {
          mode: 'index',
          intersect: true,
        },
        plugins: {
          legend: {
            labels: {
              color: this.legendLabelColor,
            },
          },
          tooltip: {
            callbacks: {
              title: (data) => {
                const label = data[0].label;
                const labelIndex = data[0].chart.data.labels.indexOf(label);
                const nextHour =
                  data[0].chart.data.labels[labelIndex + 1] || labels[0];

                return `${label} - ${nextHour}`;
              },
              label: (context) => {
                if (context.dataset.yAxisID === 'y1') {
                  return t('js.charts.alerts.response_time_value', {
                    label: context.dataset.label,
                    value: context.formattedValue,
                  });
                }

                return `${context.dataset.label}: ${context.formattedValue}`;
              },
            },
          },
        },
        scales: {
          x: {
            steps: 1,
            beginAtZero: true,
            title: {
              display: true,
              text: t('js.charts.common.hour'),
              color: this.chartLabelColor,
            },
            grid: {
              display: true,
              color: this.gridColor,
              borderColor: this.gridColor,
            },
            ticks: {
              color: this.chartLabelColor,
            },
          },
          y: {
            title: {
              display: true,
              text: t('js.charts.alerts.alerts_alarms'),
              color: this.chartLabelColor,
            },
            ticks: {
              precision: 0,
              color: this.chartLabelColor,
            },
            grid: {
              display: false,
            },
          },
          y1: {
            title: {
              display: true,
              text: t('js.charts.alerts.response_time_mins'),
              color: this.responseTimesColor,
              font: {
                weight: 'bold',
                color: this.responseTimesColor,
              },
            },
            type: 'linear',
            display: true,
            position: 'right',
            grid: {
              display: true,
              color: this.gridColor,
              borderColor: this.gridColor,
            },
            ticks: {
              precision: 0,
              color: this.responseTimesColor,
              font: {
                weight: 'bold',
              },
            },
          },
        },
      },
      data: {
        labels: labels,
        datasets: [
          {
            label: t('js.charts.alerts.alerts'),
            backgroundColor: '#EC8333',
            yAxisID: 'y',
            order: 3,
            datalabels: {
              display: false,
            },
            data: seriesByHour(this.dataValue.alerts),
          },
          {
            label: t('js.charts.alerts.alarms'),
            backgroundColor: '#A3181F',
            yAxisID: 'y',
            order: 2,
            datalabels: {
              display: false,
            },
            data: seriesByHour(this.dataValue.alarms),
          },
          {
            label: t('js.charts.alerts.response_time'),
            type: 'line',
            backgroundColor: this.responseTimesColor,
            borderColor: this.responseTimesColor,
            borderCapStyle: 'round',
            pointRadius: 0,
            yAxisID: 'y1',
            order: 1,
            data: seriesByHour(this.dataValue.response_times),
          },
        ],
      },
    });
  }

  get legendLabelColor() {
    return currentTheme() === 'light' ? COLOR_BLACK : COLOR_GRAY;
  }

  get chartLabelColor() {
    return currentTheme() === 'light' ? COLOR_GRAY_DARK : COLOR_GRAY;
  }

  get gridColor() {
    return currentTheme() === 'light' ? COLOR_GRAY_LIGHT : COLOR_GRAY_DARK;
  }

  get responseTimesColor() {
    return currentTheme() === 'light' ? COLOR_BLUE : COLOR_BLUE_LIGHT;
  }
}
